import {type ReactNode, useRef, Suspense, useMemo} from 'react';
import {Disclosure, Listbox} from '@headlessui/react';
import {defer, type LoaderArgs, redirect} from '@shopify/remix-oxygen';
import {
  useLoaderData,
  Await,
  useSearchParams,
  useLocation,
  useTransition,
} from '@remix-run/react';
import React from 'react';
import {
  AnalyticsPageType,
  Money,
  ShopifyAnalyticsProduct,
  ShopPayButton,
} from '@shopify/hydrogen';
import {
  Heading,
  IconCaret,
  IconCheck,
  IconClose,
  ProductGallery,
  ProductSwimlane,
  Section,
  Skeleton,
  Text,
  Link,
  AddToCartButton,
  Button,
} from '~/components';
import {getExcerpt} from '~/lib/utils';
import {seoPayload, canonicalUrl} from '~/lib/seo.server';
import {legacyRedirectTarget} from '~/lib/legacyRedirects';
import {optionsFromSearch} from '~/lib/variantUrl';
import {findProductVariant} from '~/lib/productSelection';
import invariant from 'tiny-invariant';
import {LRUCache} from 'lru-cache';
import clsx from 'clsx';
import type {
  ProductVariant,
  Product as ProductType,
  Shop,
  ProductConnection,
  CollectionConnection,
} from '@shopify/hydrogen/storefront-api-types';
import {MEDIA_FRAGMENT, PRODUCT_CARD_FRAGMENT} from '~/data/fragments';
import type {Storefront} from '~/lib/type';
import type {Product} from 'schema-dts';
import {routeHeaders, CACHE_SHORT} from '~/data/cache';
import type {LinksFunction} from '@shopify/remix-oxygen';

//components
import ProductDetails from 'components/productDetails';
import ProductDetailsRecommendations from 'components/productDetails/ProductDetailsRecommendations';
import GoogleReviews from 'components/modules/GoogleReviews';
import FacebookReviews from 'components/modules/FacebookReviews';
import LooxReviews from 'components/modules/LooxReviews';
import Container from 'components/Container';

//domains
import {
  useCollectionQuery,
  getCollectionsForBreadcrumb,
} from 'domains/collections';
import {
  SanityProductQueryResponse,
  SanityProductSettings,
  useProductQuery as fetchProductData,
  getProductSettings,
} from 'domains/product';
import {getLangagueCodeForSanityQuery, isTWSite} from '~/domains/sanity';
import {trackEvent} from '~/utils/gtags.client';
//translation
import translation from 'translations/global.json';
import {routeLocale} from 'domains/global';

const cache = new LRUCache<string, SanityProductSettings>({
  max: 500,
  ttl: 1000 * 60 * 60 * 24,
});

export const headers = routeHeaders;

// PreProduct used to be listed here, which made <ExternalScripts> emit a
// second copy on every product page as a plain async <script>. It now loads
// once, after hydration, from the root App — see the loader effect there.
// ProductDetailsPreorder still adds its own copy on pre-order products.
const scripts: any = () => {
  return [];
};

export const handle = {scripts};

export const links: LinksFunction = () => {
  return [
    {
      rel: 'stylesheet',
      href: 'https://cdn.jsdelivr.net/npm/swiper@8/swiper-bundle.min.css',
    },
  ];
};

export async function loader({params, request, context}: LoaderArgs) {
  const {language, country} = context.storefront.i18n;

  const {productHandle} = params;
  invariant(productHandle, 'Missing productHandle param, check route filename');

  const searchParams = new URL(request.url).searchParams;

  const {shop, product: queriedProduct} = await context.storefront.query<{
    product: ProductType & {selectedVariant?: ProductVariant};
    shop: Shop;
  }>(PRODUCT_QUERY, {
    variables: {
      handle: productHandle,
      country: isTWSite(request.url) ? 'TW' : 'HK',
      language: context.storefront.i18n.language,
    },
  });

  if (!queriedProduct?.id) {
    // Renamed handles live in the ported Shopify redirect table — the old
    // store's own mappings, which the headless storefront never had.
    const legacy = legacyRedirectTarget(new URL(request.url).pathname);
    if (legacy) {
      throw redirect(legacy, 301);
    }
    throw new Response(null, {status: 404});
  }

  // Only actual option names can select a variant. A tagged product URL
  // used to send utm_*, fbclid, etc. to variantBySelectedOptions, preventing
  // Shopify from resolving the requested size/colour.
  const options = optionsFromSearch(
    searchParams.toString(),
    queriedProduct.options.map((option) => option.name),
  );
  const selectedOptions = Object.entries(options).map(([name, value]) => ({
    name,
    value: value as string,
  }));
  let requestedVariant = findProductVariant(
    queriedProduct.variants.nodes,
    options,
  );

  // The page's main query loads up to 100 variants. Resolve an explicit
  // selection beyond that window without fetching another full product.
  if (
    !requestedVariant &&
    selectedOptions.length === queriedProduct.options.length
  ) {
    const selection = await context.storefront.query<{
      product: {selectedVariant?: ProductVariant} | null;
    }>(SELECTED_VARIANT_QUERY, {
      variables: {
        handle: productHandle,
        selectedOptions,
        country: isTWSite(request.url) ? 'TW' : 'HK',
        language,
      },
    });
    requestedVariant = selection.product?.selectedVariant;
  }

  const selectedVariant =
    requestedVariant ??
    queriedProduct.variants.nodes.find((variant) => variant.availableForSale) ??
    queriedProduct.variants.nodes[0];
  invariant(selectedVariant, 'Product has no variants');
  // Keep visible price, availability, analytics and structured data on the
  // same variant, including an explicitly selected sold-out variant.
  const product = {...queriedProduct, selectedVariant};
  const recommended = getRecommendedProducts(context.storefront, product.id);

  const productAnalytics: ShopifyAnalyticsProduct = {
    productGid: product.id,
    variantGid: selectedVariant.id,
    name: product.title,
    variantName: selectedVariant.title,
    brand: product.vendor,
    price: selectedVariant.price.amount,
  };

  // Product settings (buy-box labels, collection tree) depend only on the
  // language, so they are cached per language. The whole Sanity response
  // used to sit under one fixed key, served to any visitor whose
  // previous-lang cookie matched the page: /en pages showed the Chinese
  // labels, and every product got whichever product's size guide and
  // bundles were cached last.
  const languageCode = getLangagueCodeForSanityQuery(language);
  const productSettingsKey = `productSettings:${languageCode}`;
  const productQuery = fetchProductData({
    vendor: product.vendor,
    handle: product.handle,
  });
  let productSettings: SanityProductSettings | undefined =
    cache.get(productSettingsKey);
  if (!productSettings) {
    productSettings = await getProductSettings({languageCode});
    cache.set(productSettingsKey, productSettings, {ttl: 1000 * 60 * 60});
  }
  const sanityProductQueryResponse: SanityProductQueryResponse = {
    ...(await productQuery),
    productSettings,
  };

  const collectionIdsForBreadcrumb = getCollectionsForBreadcrumb({
    collections: product.collections.nodes,
    collectionStructure:
      sanityProductQueryResponse.productSettings.collection_structure,
  });
  let collectionsForBreadcrumb;

  if (collectionIdsForBreadcrumb?.map((c) => c.id)) {
    try {
      if (language === 'EN') {
        const {collections} = await context.storefront.query<{
          collections: CollectionConnection['nodes'];
        }>(
          `#graphql
        query breadcrumbCollectionsInEnglish($gids: [ID!]!)
        {
          collections: nodes(ids: $gids) {
            ...on Collection{
              id
              title
              handle
            }
          }
        }
      `,
          {
            variables: {
              gids: collectionIdsForBreadcrumb?.map((c) => c.id) ?? [''],
            },
          },
        );
        collectionsForBreadcrumb = collections;
      } else {
        const {collections} = await context.storefront.query<{
          collections: CollectionConnection['nodes'];
        }>(
          `#graphql
        query breadcrumbCollectionsInCantonese($country: CountryCode, $language: LanguageCode, $gids: [ID!]!)
        @inContext(country: $country, language: $language) {
          collections: nodes(ids: $gids) {
            ...on Collection{
              id
              title
              handle
            }
          }
        }
      `,
          {
            variables: {
              country,
              language,
              gids: collectionIdsForBreadcrumb?.map((c) => c.id) ?? [''],
            },
          },
        );
        collectionsForBreadcrumb = collections;
      }
    } catch (e) {
      console.log(e);
    }
  }

  // No aggregateRating in the Product JSON-LD. It came from the Loox
  // metafields, frozen since Loox was uninstalled, and the page shows no
  // per-product rating: Google requires the rating to be visible on the
  // page, so marking up one that isn't risks a manual action.
  const seo = seoPayload.product({
    product,
    selectedVariant,
    url: canonicalUrl(request.url),
    // The visible breadcrumb already walks the real collection hierarchy
    // (主頁 / 騎士裝備 / 頭盔 / 全罩式頭盔). The JSON-LD used to claim a flat
    // "Products → title" trail whose first item pointed at /products, which
    // 404s — a mismatch Google treats as a policy violation and suppresses
    // the rich result for.
    breadcrumb: (collectionsForBreadcrumb ?? [])
      .filter((c) => Boolean(c?.title && c?.handle))
      .map((c) => ({title: c.title as string, handle: c.handle as string})),
  });

  const selectedLocale =
    context.storefront.i18n.language.toLowerCase() as routeLocale;

  return defer(
    {
      product,
      shop,
      storeDomain: shop.primaryDomain.url,
      recommended,
      analytics: {
        pageType: AnalyticsPageType.product,
        resourceId: product.id,
        products: [productAnalytics],
        totalValue: parseFloat(selectedVariant.price.amount),
      },
      translation: translation[selectedLocale],
      collectionsForBreadcrumb,
      seo,
      // collection_structure (~100KB) is only used server-side above for
      // breadcrumbs — never ship it to the client.
      productSettings: {
        ...sanityProductQueryResponse.productSettings,
        collection_structure: undefined,
      },
      sizeGuide: sanityProductQueryResponse.sizeGuide,
      bundles: sanityProductQueryResponse.sanity_product.bundles,
    },
    {
      headers: {
        'Cache-Control': CACHE_SHORT,
      },
    },
  );
}

export default function ProductDetailsPage() {
  const {product, shop, recommended, translation} =
    useLoaderData<typeof loader>();
  // const {media, title, vendor, descriptionHtml} = product;
  // const {shippingPolicy, refundPolicy} = shop;

  // GA4 ecommerce funnel: view_item on every product view.
  React.useEffect(() => {
    if (!product?.id) return;
    const variant = product.selectedVariant;
    trackEvent('view_item', {
      currency: variant?.price?.currencyCode,
      value: variant?.price?.amount
        ? parseFloat(variant.price.amount)
        : undefined,
      items: [
        {
          item_id: product.id,
          item_name: product.title,
          item_brand: product.vendor,
        },
      ],
    });
  }, [product.id, product.selectedVariant, product.title, product.vendor]);

  return (
    <>
      {/* Server-rendered so the title/price/description/images reach crawlers
          — AI crawlers don't run JS, and the page previously served them a
          body with no product content at all. The Suspense wrapper is a crash
          firewall: with renderToReadableStream, an unexpected server render
          error inside a boundary streams the fallback and client-renders
          (the pre-SSR behavior), whereas an error outside any boundary would
          500 the page. ProductDetails never suspends, so the fallback is
          inert in normal operation. */}
      <Suspense fallback={<Skeleton className="h-32" />}>
        <ProductDetails />
      </Suspense>
      <Suspense fallback={<Skeleton className="h-32" />}>
        <Await
          errorElement="There was a problem loading related products"
          resolve={recommended}
        >
          {(products) => (
            <ProductDetailsRecommendations
              products={products.filter((p, i) => i !== 0)}
            />
          )}
        </Await>
        <section className="">
          <header className="text-center mb-4">
            <h2 className="text-xl md:text-section font-bold uppercase tracking-wide">
              {translation.customer_reviews_title ?? 'Customer Reviews'}
            </h2>
          </header>
          <Container>
            <LooxReviews
              module={{
                _type: 'module.looxReviews',
                _key: 'product-page-loox-reviews',
                active: true,
              }}
            />
            <GoogleReviews />
            <FacebookReviews />
          </Container>
        </section>
      </Suspense>
    </>
  );
}

const PRODUCT_VARIANT_FRAGMENT = `#graphql
  fragment ProductVariantFragment on ProductVariant {
    id
    availableForSale
    quantityAvailable
    selectedOptions {
      name
      value
    }
    image {
      id
      url
      altText
      width
      height
    }
    price {
      amount
      currencyCode
    }
    compareAtPrice {
      amount
      currencyCode
    }
    sku
    title
    unitPrice {
      amount
      currencyCode
    }
    product {
      title
      handle
    }
  }
`;

const SELECTED_VARIANT_QUERY = `#graphql
  ${PRODUCT_VARIANT_FRAGMENT}
  query ProductSelection(
    $country: CountryCode
    $language: LanguageCode
    $handle: String!
    $selectedOptions: [SelectedOptionInput!]!
  ) @inContext(country: $country, language: $language) {
    product(handle: $handle) {
      selectedVariant: variantBySelectedOptions(selectedOptions: $selectedOptions) {
        ...ProductVariantFragment
      }
    }
  }
`;

const PRODUCT_QUERY = `#graphql
  ${MEDIA_FRAGMENT}
  ${PRODUCT_VARIANT_FRAGMENT}
  query Product(
    $country: CountryCode
    $language: LanguageCode
    $handle: String!
  ) @inContext(country: $country, language: $language) {
    product(handle: $handle) {
      id
      tags
      title
      vendor
      handle
      descriptionHtml
      description
      options {
        name
        values
      }
      productType
      collections(first: 30) {
        nodes {
          title
          id
          shipping_status: metafield(
            namespace: "collection_metadata"
            key: "shipping_status"
          ) {
            value
            type
          }
          is_brand: metafield(
            namespace: "collection_metadata"
            key: "is_brand"
          ) {
            value
            type
          }
          size_chart: metafield(
            namespace: "collection_metadata"
            key: "size_chart"
          ) {
            value
            type
          }
        }
      }
      media(first: 20) {
        nodes {
          ...Media
        }
      }
      variants(first: 100) {
        nodes {
          ...ProductVariantFragment
        }
      }
      seo {
        description
        title
      }
    }
    shop {
      name
      primaryDomain {
        url
      }
      shippingPolicy {
        body
        handle
      }
      refundPolicy {
        body
        handle
      }
    }
  }
`;

// Related-product cards read title, handle, vendor, tags, options and the
// variants (colour swatches need every variant's selectedOptions, image,
// availability and price). They never read media, quantityAvailable, sku or
// unitPrice — yet the query shipped all of it, 20 products deep, as a 189KB
// deferred chunk streamed into every product page's HTML.
const RECOMMENDED_PRODUCTS_QUERY = `#graphql
  query productRecommendations(
    $productId: ID!
    $count: Int
    $country: CountryCode
    $language: LanguageCode
  ) @inContext(country: $country, language: $language) {
    recommended: productRecommendations(productId: $productId) {
      id
      title
      vendor
      handle
      publishedAt
      options {
        name
        values
      }
      tags
      variants(first: 100) {
        nodes {
          id
          title
          availableForSale
          selectedOptions {
            name
            value
          }
          image {
            url
            altText
            width
            height
          }
          price {
            amount
            currencyCode
          }
          compareAtPrice {
            amount
            currencyCode
          }
        }
      }
    }
    additional: products(first: $count, sortKey: BEST_SELLING) {
      nodes {
        id
        title
        vendor
        handle
        publishedAt
        options {
          name
          values
        }
        tags
        variants(first: 100) {
          nodes {
            id
            title
            availableForSale
            selectedOptions {
              name
              value
            }
            image {
              url
              altText
              width
              height
            }
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
          }
        }
      }
    }
  }
`;

async function getRecommendedProducts(
  storefront: Storefront,
  productId: string,
) {
  const products = await storefront.query<{
    recommended: ProductType[];
    additional: ProductConnection;
  }>(RECOMMENDED_PRODUCTS_QUERY, {
    variables: {productId, count: 12},
  });

  invariant(products, 'No data returned from Shopify API');

  const mergedProducts = products.recommended
    .concat(products.additional.nodes)
    .filter(
      (value, index, array) =>
        array.findIndex((value2) => value2.id === value.id) === index,
    );

  const originalProduct = mergedProducts
    .map((item: ProductType) => item.id)
    .indexOf(productId);

  mergedProducts.splice(originalProduct, 1);

  return mergedProducts;
}
